import { useFormikContext } from 'formik';
import { Box, Card, Stack, Typography } from '@mui/material';
import {
  theme,
  ANNOUNCEMENT_TYPE,
  BANNER_COLORS,
  BANNER_ICONS,
  Icon,
} from 'bs-unified-ui';
import { Field } from '~/components/common/Formik2';
import {
  ANNOUNCEMENT_TYPE_OPTIONS,
  DISMISS_BEHAVIOR_POPUP_OPTIONS,
  DISMISS_BEHAVIOR_BANNER_OPTIONS,
  DISMISS_BEHAVIOR_DESCRIPTIONS,
  BANNER_COLOR_DEFAULT,
} from '~/constants';
import { useAnnouncementDetailContext } from '../AnnouncementDetailContext';

const FormatSection = () => {
  const { values, setFieldValue } = useFormikContext<any>();
  const { isArchived } = useAnnouncementDetailContext();

  const handleTypeChange = (newType: string) => {
    // Check if dismiss_behaviors is valid for new type
    const isPopupType =
      newType === ANNOUNCEMENT_TYPE.POP_UP ||
      newType === ANNOUNCEMENT_TYPE.RICH_POP_UP;
    const isBannerType =
      newType === ANNOUNCEMENT_TYPE.GLOBAL_BANNER ||
      newType === ANNOUNCEMENT_TYPE.HOME_PAGE_BANNER;

    let dismissOptions = [];
    if (isPopupType) {
      dismissOptions = DISMISS_BEHAVIOR_POPUP_OPTIONS;
    } else if (isBannerType) {
      dismissOptions = DISMISS_BEHAVIOR_BANNER_OPTIONS;
    }

    const isValidDismissBehavior = dismissOptions.some(
      (opt) => opt.value === values.dismiss_behaviors,
    );
    if (!isValidDismissBehavior && dismissOptions.length > 0) {
      setFieldValue('dismiss_behaviors', dismissOptions[0].value);
    }

    // Check if banner_color is valid for new type
    if (isBannerType) {
      let validColors = [];
      if (newType === ANNOUNCEMENT_TYPE.GLOBAL_BANNER) {
        validColors = BANNER_COLORS.GLOBAL_BANNER;
      } else {
        validColors = BANNER_COLORS.HOME_PAGE_BANNER;
      }
      const isValidColor = validColors.includes(values.banner_color);
      if (!isValidColor) {
        setFieldValue('banner_color', BANNER_COLOR_DEFAULT[newType]);
      }
    }
  };

  const isPopupType =
    values.type === ANNOUNCEMENT_TYPE.POP_UP ||
    values.type === ANNOUNCEMENT_TYPE.RICH_POP_UP;
  const isBannerType =
    values.type === ANNOUNCEMENT_TYPE.GLOBAL_BANNER ||
    values.type === ANNOUNCEMENT_TYPE.HOME_PAGE_BANNER;

  // eslint-disable-next-line no-nested-ternary
  const dismissBehaviorOptions = isPopupType
    ? DISMISS_BEHAVIOR_POPUP_OPTIONS
    : isBannerType
    ? DISMISS_BEHAVIOR_BANNER_OPTIONS
    : [];

  const renderBannerColor = () => {
    // eslint-disable-next-line no-nested-ternary
    const colors = isBannerType
      ? values.type === ANNOUNCEMENT_TYPE.GLOBAL_BANNER
        ? BANNER_COLORS.GLOBAL_BANNER
        : BANNER_COLORS.HOME_PAGE_BANNER
      : [];

    if (colors.length === 0) {
      return null;
    }

    return (
      <Box>
        <Typography
          sx={{
            fontSize: 14,
            marginBottom: 1,
            color: theme.foreground.black,
          }}
        >
          Banner color
        </Typography>
        <Stack direction='row' spacing={1} alignItems='center' display='flex'>
          {colors.map((color) => (
            <Box
              key={color}
              onClick={() =>
                !isArchived && setFieldValue('banner_color', color)
              }
              sx={{
                borderRadius: '4px',
                cursor: isArchived ? 'default' : 'pointer',
                borderWidth: '1px',
                borderStyle: 'solid',
                borderColor:
                  values.banner_color === color ? `#2D91FC` : 'white',
                padding: '8px',
                display: 'flex',
                pointerEvents: isArchived ? 'none' : 'auto',
              }}
            >
              <span
                style={{
                  width: 16,
                  height: 16,
                  backgroundColor: color,
                  borderRadius: '100%',
                  display: 'inline-block',
                }}
              />
            </Box>
          ))}
        </Stack>
      </Box>
    );
  };

  const renderIcon = () => {
    const icons = isBannerType ? BANNER_ICONS : [];

    if (icons.length === 0) {
      return null;
    }

    return (
      <Box>
        <Typography
          sx={{
            fontSize: 14,
            marginBottom: 1,
            color: theme.foreground.black,
          }}
        >
          Icon
        </Typography>
        <Stack direction='row' spacing={1} alignItems='center' display='flex'>
          {icons.map((ic, index) => (
            <Box
              key={ic}
              onClick={() => !isArchived && setFieldValue('icon', ic)}
              sx={{
                borderRadius: '4px',
                cursor: isArchived ? 'default' : 'pointer',
                borderWidth: '1px',
                borderStyle: 'solid',
                borderColor: values.icon === ic ? `#2D91FC` : 'white',
                padding: '8px',
                display: 'flex',
                alignItems: 'center',
                pointerEvents: isArchived ? 'none' : 'auto',
              }}
            >
              <Icon size={18} name={ic} unset />
              {index === 0 && 'No icon'}
            </Box>
          ))}
        </Stack>
      </Box>
    );
  };

  return (
    <Card
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        color: theme.foreground.black,
      }}
    >
      <Typography
        sx={{
          fontWeight: 600,
          fontSize: 18,
        }}
      >
        Format
      </Typography>
      <Field.Select
        name='type'
        label='Type of announcement'
        fullWidth
        resizePopover
        checkedValue
        options={ANNOUNCEMENT_TYPE_OPTIONS}
        onChange={handleTypeChange}
        sx={isArchived ? { pointerEvents: 'none' } : {}}
      />
      <Box>
        <Field.Select
          name='dismiss_behaviors'
          label='Dismiss behaviors'
          fullWidth
          resizePopover
          checkedValue
          options={dismissBehaviorOptions}
          sx={isArchived ? { pointerEvents: 'none' } : {}}
        />
        {values.dismiss_behaviors && (
          <Typography
            sx={{
              color: 'rgba(51, 54, 61, 0.40)',
              fontSize: 14,
              marginTop: 1,
            }}
          >
            {DISMISS_BEHAVIOR_DESCRIPTIONS[values.dismiss_behaviors]}
          </Typography>
        )}
      </Box>
      {renderBannerColor()}
      {renderIcon()}
    </Card>
  );
};

export default FormatSection;
